/**
 * The collection: every filed card with its shelf address.
 *
 * One table, searched, filtered and sorted on the device - the rows arrive in
 * walk order and never leave it, so narrowing them is local and instant. The
 * address column is the point: this is the collection with its locations, and
 * a row without one would be a card that cannot be found again.
 *
 * Each row carries its copy id, so the table can act on what it shows: a
 * checkbox names the cards a delete removes, and the name opens the copy
 * itself - its picture, its shelf, and the three fields a person may change.
 */

import { useMemo, useState, type MouseEvent } from 'react';
import { createPortal } from 'react-dom';
import type { Api } from '../api/http.js';
import { scryfallImageUrl } from '../api/images.js';
import type { CollectionCopy } from '../api/types.js';
import { Banner } from '../components/Banner.js';
import { CopyModal } from '../components/CopyModal.js';
import { useAsync } from '../state/useAsync.js';

export interface CollectionScreenProps {
  api: Api;
}

type SortKey = 'name' | 'set' | 'number' | 'rarity' | 'finish' | 'condition' | 'address';

const COLUMNS: ReadonlyArray<{ key: SortKey; label: string }> = [
  { key: 'name', label: 'Name' },
  { key: 'set', label: 'Set' },
  { key: 'number', label: 'No.' },
  { key: 'rarity', label: 'Rarity' },
  { key: 'finish', label: 'Finish' },
  { key: 'condition', label: 'Condition' },
  { key: 'address', label: 'Location' },
];

const RARITY_NAMES: Record<string, string> = { C: 'Common', U: 'Uncommon', R: 'Rare', M: 'Mythic' };
const RARITY_RANK: Record<string, number> = { C: 0, U: 1, R: 2, M: 3 };

const rarityLabel = (rarity: string | null): string => {
  if (rarity === null || rarity === '') return '—';
  return RARITY_NAMES[rarity.toUpperCase()] ?? rarity;
};

const rarityRank = (rarity: string | null): number => {
  if (rarity === null || rarity === '') return -1;
  return RARITY_RANK[rarity.toUpperCase()] ?? 99;
};

function cellValue(row: CollectionCopy, key: SortKey): string | number | boolean {
  switch (key) {
    case 'name':
      return row.name;
    case 'set':
      return row.setCode;
    case 'number':
      return row.collectorNumber;
    case 'rarity':
      return rarityRank(row.rarity);
    case 'finish':
      return row.foil;
    case 'condition':
      return row.condition;
    case 'address':
      return row.address;
  }
}

function compareRows(left: CollectionCopy, right: CollectionCopy, key: SortKey): number {
  const leftValue = cellValue(left, key);
  const rightValue = cellValue(right, key);
  if (typeof leftValue === 'boolean' || typeof rightValue === 'boolean') {
    return Number(leftValue) - Number(rightValue);
  }
  if (typeof leftValue === 'number' || typeof rightValue === 'number') {
    return Number(leftValue) - Number(rightValue);
  }
  return String(leftValue).localeCompare(String(rightValue), undefined, { numeric: true, sensitivity: 'base' });
}

export function CollectionScreen({ api }: CollectionScreenProps) {
  const [collection, reload] = useAsync(() => api.collection(), [api]);
  const [query, setQuery] = useState('');
  const [setFilter, setSetFilter] = useState('');
  const [rarityFilter, setRarityFilter] = useState('');
  const [finishFilter, setFinishFilter] = useState('');
  const [conditionFilter, setConditionFilter] = useState('');
  const [sort, setSort] = useState<{ key: SortKey; descending: boolean }>({ key: 'address', descending: false });
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [busy, setBusy] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [openRow, setOpenRow] = useState<CollectionCopy | null>(null);
  const [preview, setPreview] = useState<{ src: string; x: number; y: number } | null>(null);

  const copies = collection.status === 'ready' ? collection.value.copies : [];
  const total = collection.status === 'ready' ? collection.value.count : 0;

  const sets = useMemo(() => [...new Set(copies.map((row) => row.setCode).filter((code) => code !== ''))].sort(), [copies]);
  const rarities = useMemo(
    () =>
      [...new Set(copies.map((row) => row.rarity).filter((rarity): rarity is string => rarity !== null && rarity !== ''))].sort(
        (left, right) => rarityRank(left) - rarityRank(right),
      ),
    [copies],
  );
  const conditions = useMemo(() => [...new Set(copies.map((row) => row.condition))].sort(), [copies]);

  const rows = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const filtered = copies.filter((row) => {
      if (needle !== '') {
        const haystack = [row.name, row.setCode, row.collectorNumber, row.address, row.containerLabel, row.binLabel]
          .join(' ')
          .toLowerCase();
        if (!haystack.includes(needle)) return false;
      }
      if (setFilter !== '' && row.setCode !== setFilter) return false;
      if (rarityFilter !== '' && row.rarity !== rarityFilter) return false;
      if (finishFilter === 'foil' && !row.foil) return false;
      if (finishFilter === 'nonfoil' && row.foil) return false;
      if (conditionFilter !== '' && row.condition !== conditionFilter) return false;
      return true;
    });
    filtered.sort((left, right) => {
      const order = compareRows(left, right, sort.key);
      return sort.descending ? -order : order;
    });
    return filtered;
  }, [copies, query, setFilter, rarityFilter, finishFilter, conditionFilter, sort]);

  const filtersActive =
    query.trim() !== '' || setFilter !== '' || rarityFilter !== '' || finishFilter !== '' || conditionFilter !== '';

  const resetFilters = (): void => {
    setQuery('');
    setSetFilter('');
    setRarityFilter('');
    setFinishFilter('');
    setConditionFilter('');
  };

  const toggleSort = (key: SortKey): void => {
    setSort((current) =>
      current.key === key ? { key, descending: !current.descending } : { key, descending: false },
    );
  };

  const toggleOne = (publicId: string): void =>
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(publicId)) next.delete(publicId);
      else next.add(publicId);
      return next;
    });

  const toggleAllVisible = (): void =>
    setSelected((current) => {
      const visible = rows.map((row) => row.publicId);
      const allSelected = visible.length > 0 && visible.every((id) => current.has(id));
      if (allSelected) {
        const next = new Set(current);
        for (const id of visible) next.delete(id);
        return next;
      }
      return new Set([...current, ...visible]);
    });

  const remove = async (): Promise<void> => {
    if (selected.size === 0) return;
    setBusy(true);
    setDeleteError(null);
    let failures = 0;
    for (const publicId of selected) {
      try {
        await api.deleteCopy(publicId);
      } catch {
        failures += 1;
      }
    }
    setSelected(new Set());
    setConfirmingDelete(false);
    setBusy(false);
    if (failures > 0) setDeleteError(`${failures} card${failures === 1 ? '' : 's'} could not be deleted.`);
    reload();
  };

  const showPreview = (row: CollectionCopy, event: MouseEvent): void => {
    const src = scryfallImageUrl(row, 'normal');
    if (src === null) return;
    // The preview is portalled to the body, so these viewport coordinates land
    // exactly beside the cursor no matter which animated ancestor the table
    // sits in. It flips above the cursor near the bottom edge, clear of the
    // tab bar.
    const x = Math.min(event.clientX + 18, window.innerWidth - 220);
    const below = event.clientY + 18;
    const y = below + 300 > window.innerHeight - 90 ? Math.max(8, event.clientY - 300) : below;
    setPreview((current) => (current !== null && current.src === src && current.x === x && current.y === y ? current : { src, x, y }));
  };

  return (
    <div className="stack">
      <header className="row session-head">
        <div>
          <h2>Collection</h2>
          <p className="hint">Every filed card, with the shelf it sits on. Search narrows it; a header sorts it.</p>
        </div>
      </header>

      {collection.status === 'loading' && (
        <>
          <p role="status" aria-live="polite">
            Loading your collection…
          </p>
          <div className="card" aria-busy="true" aria-hidden="true">
            <div className="skeleton" />
          </div>
        </>
      )}
      {collection.status === 'error' && <Banner kind="error" onRetry={reload}>{collection.message}</Banner>}

      {collection.status === 'ready' && total === 0 && (
        <div className="card">
          <h3>No cards filed yet</h3>
          <p className="hint">Scan a card and it lands here, with its shelf address attached.</p>
        </div>
      )}

      {collection.status === 'ready' && total > 0 && (
        <>
          <div className="row">
            <label className="grow">
              Search the collection
              <input
                type="search"
                autoComplete="off"
                spellCheck={false}
                placeholder="Name, set, or shelf…"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                maxLength={120}
              />
            </label>
          </div>
          <div className="row filters">
            <label>
              Set
              <select value={setFilter} onChange={(event) => setSetFilter(event.target.value)} aria-label="Filter by set">
                <option value="">All sets</option>
                {sets.map((code) => (
                  <option key={code} value={code}>
                    {code.toUpperCase()}
                  </option>
                ))}
              </select>
            </label>
            {rarities.length > 0 && (
              <label>
                Rarity
                <select value={rarityFilter} onChange={(event) => setRarityFilter(event.target.value)} aria-label="Filter by rarity">
                  <option value="">All rarities</option>
                  {rarities.map((rarity) => (
                    <option key={rarity} value={rarity}>
                      {rarityLabel(rarity)}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <label>
              Finish
              <select value={finishFilter} onChange={(event) => setFinishFilter(event.target.value)} aria-label="Filter by finish">
                <option value="">Any finish</option>
                <option value="nonfoil">Non-foil</option>
                <option value="foil">Foil</option>
              </select>
            </label>
            {conditions.length > 1 && (
              <label>
                Condition
                <select
                  value={conditionFilter}
                  onChange={(event) => setConditionFilter(event.target.value)}
                  aria-label="Filter by condition"
                >
                  <option value="">Any condition</option>
                  {conditions.map((condition) => (
                    <option key={condition} value={condition}>
                      {condition}
                    </option>
                  ))}
                </select>
              </label>
            )}
            {filtersActive && (
              <button type="button" className="ghost" onClick={resetFilters}>
                Reset
              </button>
            )}
          </div>
          <div className="row session-head">
            <p className="hint tnum" role="status" aria-live="polite">
              {rows.length === total ? `${total} card${total === 1 ? '' : 's'}` : `${rows.length} of ${total} cards`}
              {selected.size > 0 && ` · ${selected.size} selected`}
            </p>
            {selected.size > 0 && (
              confirmingDelete ? (
                <span className="export">
                  <button type="button" className="danger" disabled={busy} onClick={() => void remove()}>
                    {busy ? 'Deleting…' : `Confirm delete ${selected.size} card${selected.size === 1 ? '' : 's'}`}
                  </button>
                  <button type="button" className="ghost" onClick={() => setConfirmingDelete(false)}>
                    Keep them
                  </button>
                </span>
              ) : (
                <button type="button" className="danger" onClick={() => setConfirmingDelete(true)}>
                  Delete {selected.size} card{selected.size === 1 ? '' : 's'}
                </button>
              )
            )}
          </div>
          {deleteError && <Banner kind="error">{deleteError}</Banner>}
          {rows.length === 0 ? (
            <div className="card">
              <h3>No cards match these filters.</h3>
              <p className="hint">Clear the search to see the whole collection again.</p>
              <button type="button" className="ghost" onClick={resetFilters}>
                Reset filters
              </button>
            </div>
          ) : (
            <div className="table-scroll" role="region" aria-label="Collection table" tabIndex={0}>
              <table className="collection">
                <thead>
                  <tr>
                    <th scope="col" className="cell-select">
                      <input
                        type="checkbox"
                        aria-label="Select all visible cards"
                        checked={rows.length > 0 && rows.every((row) => selected.has(row.publicId))}
                        onChange={toggleAllVisible}
                      />
                    </th>
                    {COLUMNS.map((column) => (
                      <th
                        key={column.key}
                        aria-sort={
                          sort.key === column.key ? (sort.descending ? 'descending' : 'ascending') : 'none'
                        }
                        scope="col"
                      >
                        <button type="button" className="sort" onClick={() => toggleSort(column.key)}>
                          <span>Sort by {column.label.toLowerCase()}</span>
                          <span aria-hidden="true">{sort.key === column.key ? (sort.descending ? ' ▼' : ' ▲') : ''}</span>
                        </button>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.publicId} className={selected.has(row.publicId) ? 'selected' : undefined}>
                      <td className="cell-select">
                        <input
                          type="checkbox"
                          aria-label={`Select ${row.name}`}
                          checked={selected.has(row.publicId)}
                          onChange={() => toggleOne(row.publicId)}
                        />
                      </td>
                      <th scope="row">
                        <button
                          type="button"
                          className="name-btn"
                          onClick={() => setOpenRow(row)}
                          onMouseEnter={(event) => showPreview(row, event)}
                          onMouseMove={(event) => showPreview(row, event)}
                          onMouseLeave={() => setPreview(null)}
                          onFocus={() => setPreview(null)}
                        >
                          {row.name}
                        </button>
                      </th>
                      <td className="tnum" data-label="Set">{row.setCode === '' ? '—' : row.setCode.toUpperCase()}</td>
                      <td className="tnum" data-label="No.">{row.collectorNumber === '' ? '—' : row.collectorNumber}</td>
                      <td data-label="Rarity">{rarityLabel(row.rarity)}</td>
                      <td data-label="Finish">{row.foil ? 'Foil' : 'Non-foil'}</td>
                      <td data-label="Condition">{row.condition}</td>
                      <td className="walk-address" data-label="Location">{row.address}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {preview &&
        createPortal(
          <div className="card-preview" style={{ left: preview.x, top: preview.y }} aria-hidden="true">
            <img src={preview.src} alt="" onError={() => setPreview(null)} />
          </div>,
          document.body,
        )}

      {openRow &&
        createPortal(
          <CopyModal
            api={api}
            row={openRow}
            onClose={() => setOpenRow(null)}
            onSaved={() => {
              setOpenRow(null);
              reload();
            }}
          />,
          document.body,
        )}
    </div>
  );
}
